import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Phone, Mail, MessageCircle, MapPin, User } from 'lucide-react';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const message = `Name: ${formData?.name}\nPhone: ${formData?.countryCode}${formData?.phone}\nMessage: ${formData?.message}`;
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <>
      <SEO
        title="Contact Us - Advait Tour and Travels | Get in Touch"
        description="Contact Advait Tour and Travels for taxi booking inquiries. Call +91-79986-66660, email Advaittourtravel666@gmail.com, or visit us in New Shivpuri, Ludhiana."
        keywords="contact advait tour and travels, taxi booking contact, chandigarh taxi contact, ludhiana taxi service, advait tour and travels phone number"
        canonicalUrl={`${window.location?.origin}/contact-us`}
        ogTitle="Contact Us - Advait Tour and Travels"
        ogDescription="Get in touch with Advait Tour and Travels for all your taxi booking needs. Available 24/7."
        ogUrl={`${window.location?.origin}/contact-us`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Us - Advait Tour and Travels",
          "description": "Contact Advait Tour and Travels for taxi booking inquiries"
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Contact Us
              </h1>
              <p className="text-xl text-white max-w-3xl mx-auto">
                We're here to help! Reach out to us anytime
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left - Contact Form */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData?.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      required
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="countryCode"
                        value={formData?.countryCode}
                        onChange={handleInputChange}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="+91">+91 (India)</option>
                        <option value="+1">+1 (USA)</option>
                        <option value="+44">+44 (UK)</option>
                        <option value="+971">+971 (UAE)</option>
                        <option value="+61">+61 (Australia)</option>
                        <option value="+65">+65 (Singapore)</option>
                      </select>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData?.phone}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                        required
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData?.message}
                      onChange={handleInputChange}
                      placeholder="Enter your message"
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Submit
                  </Button>
                </form>
              </div>

              {/* Right - Contact Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-8 text-white">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4">Hate filling out form?</h3>
                  <p className="text-lg">Let's start speak with our expert.</p>
                </div>

                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <User className="w-5 h-5" />
                      <span className="font-semibold">Managing Director & Owner</span>
                    </div>
                    <p className="text-2xl font-bold">Harsh Nagpal</p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Phone className="w-5 h-5" />
                      <span className="font-semibold">Give a ring</span>
                    </div>
                    <a
                      href="tel:+917998666660"
                      className="text-xl font-bold hover:underline block"
                    >
                      +91-79986-66660
                    </a>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Mail className="w-5 h-5" />
                      <span className="font-semibold">Get a Quote</span>
                    </div>
                    <a
                      href="mailto:Advaittourtravel666@gmail.com"
                      className="text-lg font-bold hover:underline block break-all"
                    >
                      Advaittourtravel666@gmail.com
                    </a>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MessageCircle className="w-5 h-5" />
                      <span className="font-semibold">Whatsapp</span>
                    </div>
                    <a
                      href="https://wa.me/917998666660"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl font-bold hover:underline block"
                    >
                      +91-79986-66660
                    </a>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5" />
                      <span className="font-semibold">Contact Details</span>
                    </div>
                    <p className="text-lg font-bold">
                      New Shivpuri, Ludhiana
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactUs;